export const VERT_SHADER = `#version 300 es
in vec2 a_pos;
in vec2 a_uv;
out vec2 v_uv;
uniform vec2 u_scale;
uniform vec2 u_offset;
uniform float u_rotation;
uniform vec2 u_anchor;
uniform float u_uvFlipY; // 1 = flip V (for FBO→FBO / FBO→canvas blits)
uniform float u_sampleScreen; // 1 = UV from transformed position (adjustment layers)

void main() {
  vec2 p = a_pos - u_anchor;
  float c = cos(u_rotation);
  float s = sin(u_rotation);
  p = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
  p = p * u_scale + u_offset + u_anchor;
  // map 0..1 quad to clip space
  vec2 clip = p * 2.0 - 1.0;
  clip.y = -clip.y;
  gl_Position = vec4(clip, 0.0, 1.0);
  // Adjustment layers sample the underlay at the destination pixel, not stretched mesh UVs.
  vec2 uv = mix(a_uv, p, u_sampleScreen);
  v_uv = vec2(uv.x, mix(uv.y, 1.0 - uv.y, u_uvFlipY));
}
`;

export const GRADE_FRAG_SHADER = `#version 300 es
precision highp float;

in vec2 v_uv;
out vec4 outColor;

uniform sampler2D u_tex;
uniform float u_opacity;

// Wheels (lift/gamma/gain) — rgb offsets + master
uniform vec3 u_lift;
uniform float u_liftMaster;
uniform vec3 u_gamma;
uniform float u_gammaMaster;
uniform vec3 u_gain;
uniform float u_gainMaster;

// Tonal
uniform float u_exposure;
uniform float u_contrast;
uniform float u_pivot;
uniform float u_shadows;
uniform float u_midtones;
uniform float u_highlights;
uniform float u_whites;
uniform float u_blacks;

// Color
uniform float u_saturation;
uniform float u_hue;
uniform float u_temperature;
uniform float u_tint;
uniform float u_intensity;

// Qualifier
uniform float u_qualEnabled;
uniform vec2 u_hueRange; // min,max degrees 0-360
uniform vec2 u_satRange;
uniform vec2 u_lumRange;
uniform float u_qualSoft;
uniform float u_qualInvert;
uniform float u_crossDissolve; // 0..1 mix toward black or next (simplified)

// FX
uniform float u_blur;
uniform float u_sharpen;
uniform float u_vignette;
uniform float u_glow;
uniform float u_noise;
uniform vec2 u_texelSize;

vec3 rgb2hsl(vec3 c) {
  float maxc = max(c.r, max(c.g, c.b));
  float minc = min(c.r, min(c.g, c.b));
  float l = (maxc + minc) * 0.5;
  float s = 0.0;
  float h = 0.0;
  float d = maxc - minc;
  if (d > 1e-5) {
    s = l > 0.5 ? d / (2.0 - maxc - minc) : d / (maxc + minc);
    if (maxc == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
    else if (maxc == c.g) h = (c.b - c.r) / d + 2.0;
    else h = (c.r - c.g) / d + 4.0;
    h /= 6.0;
  }
  return vec3(h * 360.0, s, l);
}

float hue2rgb(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
  if (t < 1.0/2.0) return q;
  if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
  return p;
}

vec3 hsl2rgb(vec3 hsl) {
  float h = hsl.x / 360.0;
  float s = hsl.y;
  float l = hsl.z;
  if (s < 1e-5) return vec3(l);
  float q = l < 0.5 ? l * (1.0 + s) : l + s - l * s;
  float p = 2.0 * l - q;
  return vec3(hue2rgb(p, q, h + 1.0/3.0), hue2rgb(p, q, h), hue2rgb(p, q, h - 1.0/3.0));
}

float softRange(float v, float lo, float hi, float soft) {
  float s = max(soft, 1e-4);
  float a = smoothstep(lo - s, lo + s, v);
  float b = 1.0 - smoothstep(hi - s, hi + s, v);
  return clamp(a * b, 0.0, 1.0);
}

vec3 applyWheels(vec3 c) {
  // Lift affects shadows, gain highlights, gamma midtones
  float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
  vec3 lift = u_lift + u_liftMaster;
  vec3 gamma = u_gamma + u_gammaMaster;
  vec3 gain = u_gain + u_gainMaster;
  c = c + lift * (1.0 - luma);
  c = c * (1.0 + gain * luma);
  // gamma as power curve
  vec3 g = max(vec3(0.001), 1.0 - gamma * 0.5);
  c = pow(max(c, vec3(0.0)), g);
  return c;
}

vec3 applyTonal(vec3 c) {
  // Exposure (stops)
  c *= pow(2.0, u_exposure);
  // Contrast around pivot
  c = (c - u_pivot) * u_contrast + u_pivot;
  float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float shadowW = 1.0 - smoothstep(0.0, 0.35, luma);
  float highW = smoothstep(0.55, 1.0, luma);
  float midW = 1.0 - shadowW - highW;
  c += u_shadows * shadowW;
  c += u_midtones * midW;
  c += u_highlights * highW;
  c += u_blacks * (1.0 - smoothstep(0.0, 0.15, luma));
  c += u_whites * smoothstep(0.85, 1.0, luma);
  return c;
}

vec3 applyColor(vec3 c) {
  // Temperature / tint
  c.r += u_temperature * 0.08;
  c.b -= u_temperature * 0.08;
  c.g += u_tint * 0.06;
  vec3 hsl = rgb2hsl(clamp(c, 0.0, 1.0));
  hsl.x = mod(hsl.x + u_hue, 360.0);
  hsl.y = clamp(hsl.y * u_saturation, 0.0, 2.0);
  return hsl2rgb(hsl);
}

vec3 sampleKernel(vec2 uv, float radius) {
  vec3 sum = vec3(0.0);
  float wsum = 0.0;
  for (int x = -2; x <= 2; x++) {
    for (int y = -2; y <= 2; y++) {
      float d2 = float(x * x + y * y);
      float w = exp(-d2 / 4.0);
      vec2 o = vec2(float(x), float(y)) * u_texelSize * radius;
      sum += texture(u_tex, clamp(uv + o, 0.0, 1.0)).rgb * w;
      wsum += w;
    }
  }
  return sum / max(wsum, 1e-4);
}

float hash21(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

void main() {
  vec4 src = texture(u_tex, v_uv);
  vec3 original = src.rgb;

  // Spatial FX first (on source)
  if (u_blur > 0.001) {
    float radius = mix(0.5, 6.0, clamp(u_blur, 0.0, 1.0));
    original = mix(original, sampleKernel(v_uv, radius), clamp(u_blur * 1.4, 0.0, 1.0));
  }
  if (u_sharpen > 0.001) {
    vec3 soft = sampleKernel(v_uv, 1.2);
    vec3 hi = original + (original - soft) * (1.5 + u_sharpen * 2.0);
    original = mix(original, hi, clamp(u_sharpen, 0.0, 1.0));
  }
  if (u_glow > 0.001) {
    vec3 bloom = sampleKernel(v_uv, mix(2.0, 8.0, u_glow));
    float lum = dot(bloom, vec3(0.2126, 0.7152, 0.0722));
    original += bloom * smoothstep(0.35, 0.85, lum) * u_glow * 0.85;
  }

  vec3 graded = applyWheels(original);
  graded = applyTonal(graded);
  graded = applyColor(graded);
  graded = mix(original, graded, clamp(u_intensity, 0.0, 1.0));

  float mask = 1.0;
  if (u_qualEnabled > 0.5) {
    vec3 hsl = rgb2hsl(src.rgb);
    float hm = softRange(hsl.x, u_hueRange.x, u_hueRange.y, u_qualSoft * 30.0);
    float sm = softRange(hsl.y, u_satRange.x, u_satRange.y, u_qualSoft);
    float lm = softRange(hsl.z, u_lumRange.x, u_lumRange.y, u_qualSoft);
    mask = hm * sm * lm;
    if (u_qualInvert > 0.5) mask = 1.0 - mask;
    graded = mix(src.rgb, graded, mask);
  }

  if (u_vignette > 0.001) {
    vec2 p = v_uv * 2.0 - 1.0;
    p.x *= 1.1;
    float d = length(p);
    float vig = smoothstep(0.35, 1.15, d) * u_vignette;
    graded *= 1.0 - vig * 0.92;
  }
  if (u_noise > 0.001) {
    float n = hash21(v_uv * vec2(1920.0, 1080.0) + u_noise * 17.0);
    graded += (n - 0.5) * u_noise * 0.22;
  }

  graded = mix(graded, vec3(0.0), u_crossDissolve);
  outColor = vec4(clamp(graded, 0.0, 1.0), src.a * u_opacity);
}
`;

export function compileShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Failed to create shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
}

export function createProgram(gl: WebGL2RenderingContext, vert: string, frag: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
  const program = gl.createProgram();
  if (!program) throw new Error('Failed to create program');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${info}`);
  }
  return program;
}
